<div class="editor">

  <!-- ── Toolbar ─────────────────────────────────────────────────────────── -->
  <div class="editor-toolbar">
    <input
      class="topo-name"
      type="text"
      placeholder="Topology name"
      [value]="name()"
      (input)="onNameInput($event)"
    />

    <div class="palette">
      <span class="palette-hint">Drag onto the canvas:</span>
      @for (kind of kinds; track kind) {
        <span
          class="palette-chip"
          [class]="'kind-' + kind"
          draggable="true"
          (dragstart)="onPaletteDragStart($event, kind)"
        >
          {{ kind }}
        </span>
      }
    </div>

    <button type="button" class="ctl-btn" [class.active]="linkMode()" (click)="toggleLinkMode()">
      🔗 Link
    </button>

    <div class="zoom-group">
      <button type="button" class="ctl-btn" title="Zoom in" [disabled]="!canZoomIn()" (click)="zoomIn()">+</button>
      <button type="button" class="ctl-btn" title="Zoom out" [disabled]="!canZoomOut()" (click)="zoomOut()">−</button>
      <button type="button" class="ctl-btn" title="Fit" [disabled]="!canZoomOut()" (click)="resetZoom()">⤢</button>
    </div>

    <button type="button" class="ctl-btn" [disabled]="saving()" (click)="save()">
      {{ saving() ? 'Saving…' : 'Save' }}
    </button>
    <button type="button" class="ctl-btn" (click)="cancel()">Cancel</button>
  </div>

  @if (linkMode()) {
    <div class="editor-hint">
      Click one node, then another, to link them.
      @if (linkFrom(); as from) {
        Linking from <strong>{{ from }}</strong> — click the second node, or click the
        background to abandon it.
      }
      @if (linkNotice(); as notice) {
        <span class="hint-notice">{{ notice }}</span>
      }
    </div>
  }

  @if (validationError(); as message) {
    <div class="editor-error">{{ message }}</div>
  }
  @if (error(); as message) {
    <div class="editor-error">{{ message }}</div>
  }

  <!-- ── Canvas + side form ──────────────────────────────────────────────── -->
  <div class="editor-body">
    <div
      #wrapEl
      class="canvas-wrap vg-scroll"
      (dragover)="onCanvasDragOver($event)"
      (drop)="onCanvasDrop($event)"
    >
      <svg
        #svgEl
        class="canvas"
        [class.linking]="linkMode()"
        [attr.viewBox]="viewBox()"
        preserveAspectRatio="xMidYMid meet"
        [style.width.%]="canvasWidthPct()"
        [style.height.%]="canvasHeightPct()"
        (pointerdown)="onCanvasPointerDown()"
        (pointermove)="onCanvasPointerMove($event)"
        (pointerup)="onCanvasPointerUp()"
        (pointercancel)="onCanvasPointerUp()"
      >
        @for (edge of renderedEdges(); track edge.key) {
          <line
            class="edge"
            [attr.x1]="edge.x1"
            [attr.y1]="edge.y1"
            [attr.x2]="edge.x2"
            [attr.y2]="edge.y2"
          />
        }

        @for (rendered of renderedNodes(); track rendered.node.nodeKey) {
          @if (rendered.isLeaf) {
            <g
              class="leaf"
              [class.selected]="selectedKey() === rendered.node.nodeKey"
              [class.link-source]="linkFrom() === rendered.node.nodeKey"
              (pointerdown)="onNodePointerDown($event, rendered.node)"
            >
              <circle [attr.cx]="rendered.cx" [attr.cy]="rendered.cy" [attr.r]="leafR" />
              <text class="leaf-label" [attr.x]="rendered.cx" [attr.y]="rendered.cy + 3.2">
                {{ rendered.node.label }}
              </text>
            </g>
          } @else {
            <g
              class="node"
              [class]="'kind-' + rendered.node.kind"
              [class.selected]="selectedKey() === rendered.node.nodeKey"
              [class.link-source]="linkFrom() === rendered.node.nodeKey"
              (pointerdown)="onNodePointerDown($event, rendered.node)"
            >
              <rect
                class="box"
                [attr.x]="rendered.x"
                [attr.y]="rendered.y"
                [attr.width]="rendered.w"
                [attr.height]="rendered.h"
                rx="1.2"
              />
              <title>{{ rendered.node.label }}</title>
              <text class="node-label" [attr.x]="rendered.cx" [attr.y]="rendered.firstLineY">
                @for (line of rendered.lines; track $index) {
                  <tspan [attr.x]="rendered.cx" [attr.dy]="$index === 0 ? 0 : lineH">{{ line }}</tspan>
                }
              </text>
              @if (rendered.node.sub) {
                <text class="node-sub" [attr.x]="rendered.cx" [attr.y]="rendered.subY">
                  {{ rendered.node.sub }}
                </text>
              }
            </g>
          }
        }
      </svg>
    </div>

    <aside class="side-form vg-scroll">
      @if (selected(); as node) {
        <h4>Node</h4>

        <label>
          Key
          <input type="text" [value]="node.nodeKey" (change)="onFieldInput($event, 'nodeKey')" />
        </label>

        <label>
          Label
          <input type="text" [value]="node.label" (input)="onFieldInput($event, 'label')" />
        </label>

        <label>
          Subtitle
          <input
            type="text"
            placeholder="IP, role…"
            [value]="node.sub ?? ''"
            (input)="onFieldInput($event, 'sub')"
          />
        </label>

        <label>
          Kind
          <!--
            Selection is marked on the options, not with [value] on the select: the select's
            value binding is applied before @for has created any option to match, so it finds
            nothing and the browser falls back to showing the first one.
          -->
          <select (change)="onKindChange($event)">
            @for (kind of kinds; track kind) {
              <option [value]="kind" [selected]="kind === node.kind">{{ kind }}</option>
            }
          </select>
        </label>

        @if (node.kind === 'leaf') {
          <label>
            Hangs off
            <select (change)="onParentChange($event)">
              <option value="" [selected]="node.parentKey === null">— none —</option>
              @for (candidate of parentOptions(); track candidate.nodeKey) {
                <option [value]="candidate.nodeKey" [selected]="candidate.nodeKey === node.parentKey">
                  {{ candidate.label }}
                </option>
              }
            </select>
          </label>
        }

        <label>
          Aliases
          <input
            type="text"
            placeholder="84.3.251.18, Modbus master"
            [value]="aliasesText(node)"
            (change)="onAliasesInput($event)"
          />
          <small>
            Comma-separated. Detectors name devices however they like — an alert is matched to
            this node when it mentions the key or any alias, so add every name and IP it might
            be reported under.
          </small>
        </label>

        <button type="button" class="danger" (click)="deleteSelected()">Delete node</button>
      } @else {
        <h4>Topology</h4>
        <label>
          Description
          <textarea
            rows="3"
            placeholder="What this diagram covers"
            [value]="description()"
            (input)="onDescriptionInput($event)"
          ></textarea>
        </label>
        <p class="side-hint">Select a node to edit it, or drag a kind from the toolbar.</p>
      }

      <h4 class="links-heading">
        Links
        <span class="link-count">{{ renderedEdges().length }}</span>
      </h4>

      @if (renderedEdges().length === 0) {
        <p class="side-hint">No links yet. Use 🔗 Link, then click two nodes.</p>
      } @else {
        <div class="edge-list">
          @for (edge of renderedEdges(); track edge.key) {
            <div class="edge-row">
              <div class="edge-ends">
                <span class="edge-end">{{ edge.sourceKey }}</span>
                <span class="edge-arrow">→</span>
                <span class="edge-end">{{ edge.targetKey }}</span>
              </div>
              <div class="edge-controls">
                <input
                  type="text"
                  class="edge-proto"
                  placeholder="protocol"
                  [value]="edge.proto ?? ''"
                  (change)="onProtoInput($event, edge.index)"
                />
                <button
                  type="button"
                  class="edge-remove"
                  title="Remove this link"
                  (click)="removeEdge(edge.index)"
                >
                  ✕
                </button>
              </div>
            </div>
          }
        </div>
      }
    </aside>
  </div>
</div>
